import { getPreviewId } from './previewUtils';
const scopeFor = (storylineId: string | undefined | null): string => {
  const previewId = getPreviewId();
  if (previewId) return `preview:${previewId}`;
  return `${storylineId ?? 'module'}`;
};

const keyFor = (storylineId: string | undefined | null) => `edu:player:${scopeFor(storylineId)}`;
const sigKeyFor = (storylineId: string | undefined | null) => `edu:sig:${scopeFor(storylineId)}`;

export function contentSignature(content: unknown): string {
  const source = JSON.stringify(content ?? null);
  let hash = 0;
  for (let index = 0; index < source.length; index += 1) {
    hash = (hash * 31 + source.charCodeAt(index)) | 0;
  }
  return `${source.length}:${hash}`;
}

// Records the current content signature and clears any saved player progress when the
// content has changed since it was last seen (e.g. the module was republished from the
// editor). Returns true when a change was detected. First-ever load is not a change.
export function reconcileContentSignature(
  storylineId: string | undefined | null,
  signature: string
): boolean {
  try {
    const key = sigKeyFor(storylineId);
    const stored = window.localStorage?.getItem(key) ?? null;
    const changed = stored !== null && stored !== signature;
    if (changed) clearPlayerProgress(storylineId);
    window.localStorage?.setItem(key, signature);
    return changed;
  } catch {
    return false;
  }
}

export function loadPlayerProgress<T>(storylineId: string | undefined | null): Partial<T> {
  try {
    const raw = window.localStorage?.getItem(keyFor(storylineId));
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return parsed && typeof parsed === 'object' ? (parsed as Partial<T>) : {};
  } catch {
    return {};
  }
}

export function savePlayerProgress<T>(storylineId: string | undefined | null, state: T): void {
  try {
    window.localStorage?.setItem(keyFor(storylineId), JSON.stringify(state));
  } catch {
    /* storage unavailable (private mode / sandboxed iframe) — resume is best-effort */
  }
}

export function clearPlayerProgress(storylineId: string | undefined | null): void {
  try {
    window.localStorage?.removeItem(keyFor(storylineId));
  } catch {
    /* ignore */
  }
}
